import { JobOfferState } from '@dinosband/bv-main-schema/dist/types'
import { ChatRoomLastMessageInfo, Message } from '@dinosband/bv-messenger-schema'
import { readJobOfferState } from '@dinosband/bv-main-client'
import { queryLastMessageId, queryLastMessages, queryLastUpdatedMessages, queryMessagesByIds, _matchFilter } from '@dinosband/bv-messenger-client'
import { _max, _partition, _undefined } from '@dinosband/dbi-utils'

import { BvDataCenterContext, SubscriptionManager } from '../..'
import { _isInvalidMessage } from '../../utils'
// import { _isInvalidMessage } from '../../controller/messages'
import { BvSubscriber, MessageSubscriber, MessageUpdateSubscriber, MessageDeleteSubscriber } from '../../subscriber'
import { DataCenter_StoreType } from '../abstract'
import { ChatRoomQueryFilters, MESSAGE_FILTER_UPDATED } from '../filter'
import { LAST_MESSAGE_ID, MESSAGE_ADDED, MESSAGE_DELETED, MESSAGE_UPDATED } from './const'
import { MessageCacheIndexedDB } from '@dinosband/bv-cache'

const _DEBUG_LOG = process.env.NODE_ENV !== 'production'
                   && !!process.env.VUE_APP_MESSENGER_DEBUG_LOG
                   && false

export const CHAT_ROOM_MESSAGES_DISCONTINUED = 'chat_room-messages-discontinued'
export interface MessageDiscontinuedEventPayload {
  chatRoomId: number
  added?: Message
  updated?: ChatRoomLastMessageInfo
}

let messageCache: MessageCacheIndexedDB = _undefined()

const _isChanged = (pool: Record<number, ChatRoomLastMessageInfo>) => (message: ChatRoomLastMessageInfo) => {
  const exist = pool[message.chatRoomId]
  return !exist || exist.id !== message.id || exist.updatedAt !== message.updatedAt
}

export class ChatRoomLastMessages_Module extends DataCenter_StoreType<number, Message> {

  channelId?: number
  chatRoomIds: number[] = []
  queryFilters!: ChatRoomQueryFilters

  private lastMessages: Record<number, ChatRoomLastMessageInfo> = {}
  private validMessages: Record<number, ChatRoomLastMessageInfo> = {}
  private updatedMessages: Record<number, ChatRoomLastMessageInfo> = {}

  private _initialized = false
  private _addedSubscriber?: MessageSubscriber
  private _updatedSubscriber?: MessageUpdateSubscriber
  private _deletedSubscriber?: MessageDeleteSubscriber
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _subscribers: BvSubscriber<any>[] = []

  private subscriptions = new SubscriptionManager()

  async getClient () {
    return await this.context.messengerClient.getClient()
  }

  constructor (context: BvDataCenterContext, chatRoomIds: number[], channelId?: number) {
    super(context)
    this.queryFilters = context.queryFilters
    this.chatRoomIds = chatRoomIds
    this.channelId = channelId
  }

  async init () {
    messageCache = (await import('@dinosband/bv-cache'))?.messageCache
    this._addedSubscriber = new MessageSubscriber(this.context, this.chatRoomIds, (value) => this.onMessageAdded(value))
    this._updatedSubscriber = new MessageUpdateSubscriber(this.context, this.chatRoomIds, (value) => this.onMessageUpdated(value))
    this._deletedSubscriber = new MessageDeleteSubscriber(this.context, this.chatRoomIds, (value) => this.onMessageDeleted(value))
    this._subscribers = [this._addedSubscriber, this._updatedSubscriber, this._deletedSubscriber]
    await Promise.all(this._subscribers.map((i) => i.start()))
    this.updateValids(this.chatRoomIds, 'all')
    this.initSubscriptions()
    this._initialized = true
  }

  private initSubscriptions () {
    this.subscriptions.init(this.pubsub)
    this.subscriptions.subscribe(MESSAGE_FILTER_UPDATED, (chatRoomId: number) => {
      if (this.chatRoomIds.includes(chatRoomId)) {
        this.updateValids([chatRoomId], 'changed')
      }
    })
  }

  cleanup () {
    super.cleanup()
    this.subscriptions.cleanup()
    this.lastMessages = {}
    this.chatRoomIds = []
    this._subscribers.forEach((i) => i.cleanup())
  }

  lastMessage (chatRoomId: number) {
    return this.lastMessages[chatRoomId] as Message
  }

  lastValidMessage (chatRoomId: number) {
    return this.validMessages[chatRoomId] as Message
  }

  lastUpdatedMessage (chatRoomId: number) {
    return this.updatedMessages[chatRoomId]
  }

  /*
    return: changed chat-room ids
  */
  async updateLastMessages (): Promise<number[]> {
    const result = await this.updateValids(this.chatRoomIds, 'no-publish')
    return result.map((i) => i.chatRoomId)
  }

  onReconnected () {
    this.checkDiscontinuity()
  }

  private async checkDiscontinuity () {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const adds = await this.updateAdds(this.chatRoomIds)
    const updates = await this.updateUpdates(this.chatRoomIds)
    // TODO: use updates when get valids
    const valids = await this.updateValids(this.chatRoomIds, 'no-publish')
    updates.forEach((i) => this.updatedMessages[i.chatRoomId] = i)
    // const chatRoomIds = new Set([...valids, ...updates].map((i) => i.chatRoomId))
    const chatRoomIds = this.chatRoomIds
    chatRoomIds.forEach((chatRoomId) => {
      const payload: MessageDiscontinuedEventPayload = {
        chatRoomId,
        added: _getMessage(valids, chatRoomId),
        updated: _getMessage(updates, chatRoomId),
      }
      this.pubsub.publish(CHAT_ROOM_MESSAGES_DISCONTINUED, payload)
    })
  }

  private async updateAdds (chatRoomIds: number[]) {
    const result = await this.checkAdds(chatRoomIds)
    result?.forEach((i) => this.lastMessages[i.chatRoomId] = i)
    return result
  }

  private async updateValids (chatRoomIds: number[], publish: 'all' | 'changed' | 'no-publish') {
    const changed = await this.checkValids(chatRoomIds)
    if (publish == 'all') {
      const valids = chatRoomIds.flatMap((i) => _getMessage(changed, i) ?? this.validMessages[i] ?? []) as Message[]
      this.publishValids(valids, 'all')
      return valids
    } else if (publish == 'changed') {
      // publishValids will be update validMessages
      this.publishValids(changed, 'changed')
    } else {
      changed.forEach((i) => this.validMessages[i.chatRoomId] = i)
    }
    return changed
  }

  private async updateUpdates (chatRoomIds: number[]) {
    const result = await this.checkUpdates(chatRoomIds)
    result.forEach((i) => {
      this.updatedMessages[i.chatRoomId] = i
      // TODO: invalidate cache
    })
    return result
  }

  private async checkAdds (chatRoomIds: number[]) {
    const messageInfos = await queryLastMessages(await this.getClient(), chatRoomIds, false) // only info
    if (!messageInfos) {
      return []
    }
    const addeds = messageInfos.filter(_isChanged(this.lastMessages))
    const messages = await queryMessagesByIds(await this.getClient(), addeds.map((i) => i.id))
    return messages
  }

  private async checkValids (chatRoomIds: number[]) {
    const results: Message[] = []
    const lastMessages = chatRoomIds.flatMap((i) => this.lastMessages[i] ?? []) as Message[]
    const jobOfferStates = await this.getRequiredJobOfferStates(lastMessages)
    const [valids, invalids] = _partition(lastMessages, (i) => this.isValidMessage(i, jobOfferStates))
    const changeds = valids.filter(_isChanged(this.validMessages))
    results.push(...changeds)
    if (!invalids.length) {
      return results
    }

    const incompletes: number[] = []
    await Promise.all(invalids.map(async (i) => {
      const filter = this.queryFilters.queryFilter(i.chatRoomId)
      const result = await queryLastMessageId(await this.context.messengerClient.getClient(), i.chatRoomId, filter)
      if (!result) {
        return
      }
      if (!_isChanged(this.validMessages)(result)) {
        return
      }
      const cached = await messageCache.get(result.chatRoomId, result)
      if (cached) {
        results.push(cached)
      } else {
        incompletes.push(result.id)
      }
    }))
    if (incompletes.length) {
      const completes = await queryMessagesByIds(await this.context.messengerClient.getClient(), incompletes) ?? []
      results.push(...completes)
    }
    // await updateChatRoomLastFilteredMessages(chatRoomIds)
    return results
  }

  private async checkUpdates (chatRoomIds: number[]) {
    const messages = await queryLastUpdatedMessages(await this.getClient(), chatRoomIds, false) ?? []
    const changeds = messages.filter(_isChanged(this.updatedMessages))
    return changeds
  }

  private publishValids (messages: Message[], scope: 'all' | 'changed' = 'changed') {
    const targets = scope === 'all' ? messages : messages.filter(_isChanged(this.validMessages))
    targets.forEach((i) => {
      this.validMessages[i.chatRoomId] = i
      this.pubsub.publish(MESSAGE_ADDED, i)
    })
      messages.map((i) => i.chatRoomId),
      targets.map((i) => i.chatRoomId))
    return targets
  }

  private publishLastMessageId (messages: Message[]) {
    const lastId = _max(messages.map((i) => i.id))
    if (lastId) {
      this.pubsub.publish(LAST_MESSAGE_ID, lastId)
    }
  }

  ///////////////////


  private async onMessageAdded (value: Message | Message[]) {
    const messages = Array.isArray(value) ? value : [value]
    messages.forEach((i) => {
      this.lastMessages[i.chatRoomId] = i
    })
    this.publishLastMessageId(messages)
    const jobOfferStates = this._initialized ? undefined : await this.getRequiredJobOfferStates(messages)
    const valids = messages.filter((i) => this.isValidMessage(i), jobOfferStates)
    this.publishValids(valids, 'changed')
  }

  private async onMessageUpdated (value: Message | Message[]) {
    const messages = value instanceof Array ? value : [value]
    messages.forEach(async (i) => {
      const chatRoomId = i.chatRoomId
      this.updatedMessages[chatRoomId] = i
      const last = this.lastMessages[chatRoomId]
      if (!last || last.id <= i.id) {
        this.lastMessages[chatRoomId] = i
      }
      const valid = this.validMessages[chatRoomId]
      if (!valid || valid.id <= i.id) {
        await this.updateValids([chatRoomId], 'no-publish')
      }
      const payload = { message: i, valid: this.isValidMessage(i) }
      this.pubsub.publish(MESSAGE_UPDATED, payload)
    })
  }

  private async onMessageDeleted (message: Message) {
    if (this.isValidMessage(message)) {
      this.onMessageUpdated(message)
      return
    }
    const chatRoomId = message.chatRoomId
    if (this.lastMessages[chatRoomId]?.id == message.id) {
      this.lastMessages[chatRoomId] = message
    }
    if (this.validMessages[chatRoomId]?.id == message.id) {
      await this.updateValids([chatRoomId], 'no-publish')
    }
    this.pubsub.publish(MESSAGE_DELETED, message)
  }


  // async setLastMessageInfo (info: ChatRoomLastMessageInfo) {
  //   const messageCache = (await import('@dinosband/bv-cache'))?.messageCache
  //   const cached = await messageCache.get(info.chatRoomId, info as Message)
  //   if (cached) {
  //     await setLastMessage(cached)
  //   }
  //   return cached
  // }

  isValidMessage (message: Message, jobOfferStates?: { id: number, state: JobOfferState}[]): boolean {
    const filter = this.queryFilters.queryFilter(message.chatRoomId)
    const { userId, blockedProfileIds } = this.context.mainStore
    if (_isInvalidMessage(message, userId, filter?.excludeDeleted, blockedProfileIds)) {
      return false
    }
    if (filter && (filter.excludeFinished ?? false)) {
      if (jobOfferStates?.find((i) => i.id === message.id)?.state === 'Hired') {
        return false
      }
    }
    // if (!filter?.option?.filterOwnMessages) {
    //   if (this.mainStore.profileIds.includes(message.profileId)) {
    //     return true
    //   }
    // }
    return _matchFilter(message, filter, userId)
  }

  private async getRequiredJobOfferStates (messages: Message[]) {
    const ids = messages.filter((i) => this.shouldCheckFinished(i)).map((i) => i.id)
    if (!ids.length) {
      return []
    }
    const jobOfferStates = await readJobOfferState(await this.mainStore.apollo.getClient(), ids)
    return jobOfferStates
  }

  private shouldCheckFinished (message: Message): boolean {
    const filter = this.queryFilters.queryFilter(message.chatRoomId)
    return filter && (filter.excludeFinished ?? false)
  }

}

function _getMessage (messages: Message[], chatRoomId: number): Message | undefined {
  return messages.find((i) => i.chatRoomId === chatRoomId)
}
